// FE-SCRM-060 — <TargetSearchPicker> typeahead for prospect /
// opportunity / contact / carrier picking.
//
// Example:
//
//     <TargetSearchPicker
//       value={prospect}
//       onChange={setProspect}
//       fetchResults={async (q, restrict) => api.salesSearch(q, restrict)}
//       restrictTo={["prospect"]}
//       placeholder="Pick a prospect"
//     />
//
// Wraps the FE-SCRM-054 global search endpoint via the injected
// ``fetchResults`` prop. Until -054 lands, host code can pass a
// stub returning [] or a hand-built list — the picker doesn't
// know about the API. TODO: replace the hand-rolled hook with the
// real FE-SCRM-054 hook once that chunk merges.

import {
  useCallback, useEffect, useRef, useState,
} from "react";


export type TargetKind = "prospect" | "opportunity" | "contact" | "carrier";


export interface TargetSearchResult {
  id: string;
  kind: TargetKind;
  label: string;
  /** Optional secondary text (e.g. opportunity-type, renewal month). */
  sublabel?: string;
  /** FE-SCRM-059 — opportunity rows carry their parent prospect_id so
   * consumers can auto-derive the prospect anchor when a producer
   * picks an opportunity instead of a prospect. Unused on other kinds. */
  parent_prospect_id?: string;
}


export interface TargetSearchPickerProps {
  value: TargetSearchResult | null;
  onChange: (next: TargetSearchResult | null) => void;
  fetchResults: (
    q: string,
    restrictTo: readonly TargetKind[] | undefined,
  ) => Promise<TargetSearchResult[]>;
  restrictTo?: readonly TargetKind[];
  placeholder?: string;
  debounceMs?: number;
  disabled?: boolean;
}


const DEBOUNCE_MS = 250;


export function TargetSearchPicker({
  value, onChange, fetchResults,
  restrictTo, placeholder = "Search…",
  debounceMs = DEBOUNCE_MS,
  disabled,
}: TargetSearchPickerProps): JSX.Element {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TargetSearchResult[]>([]);
  const [queried, setQueried] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelled = useRef(false);

  // React 18 StrictMode runs effect cleanups immediately after the
  // first mount in dev — without resetting the ref in setup, the
  // strict-mode cleanup pins ``cancelled.current = true`` for the
  // life of the component, swallowing every subsequent setResults
  // call and leaving the typeahead silent. Re-arm on every (re)mount.
  useEffect(() => {
    cancelled.current = false;
    return () => { cancelled.current = true; };
  }, []);

  const runSearch = useCallback(async (q: string) => {
    try {
      const rows = await fetchResults(q, restrictTo);
      if (!cancelled.current) {
        setResults(rows);
        setQueried(true);
      }
    } catch {
      if (!cancelled.current) {
        setResults([]);
        setQueried(true);
      }
    }
  }, [fetchResults, restrictTo]);

  const onTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setQuery(next);
      if (timer.current) clearTimeout(timer.current);
      if (!next.trim()) {
        setResults([]);
        setQueried(false);
        return;
      }
      timer.current = setTimeout(() => runSearch(next.trim()), debounceMs);
    },
    [debounceMs, runSearch],
  );

  const select = useCallback((row: TargetSearchResult) => {
    onChange(row);
    setQuery("");
    setResults([]);
    setQueried(false);
  }, [onChange]);

  if (value) {
    return (
      <div
        className={
          "inline-flex items-center gap-2 rounded-md border "
          + "border-border-default bg-raised px-2 py-1.5 text-sm"
        }
      >
        <span className="inline-flex items-center gap-1">
          <span
            className={
              "rounded bg-recessed px-1.5 py-0.5 text-xs font-medium "
              + "uppercase tracking-wide text-text-tertiary"
            }
          >
            {value.kind}
          </span>
          <span className="font-medium text-text-primary">{value.label}</span>
          {value.sublabel && (
            <span className="text-text-secondary">· {value.sublabel}</span>
          )}
        </span>
        <button
          type="button"
          aria-label="Clear selection"
          disabled={disabled}
          onClick={() => onChange(null)}
          className={
            "rounded p-0.5 text-text-tertiary hover:bg-recessed "
            + "hover:text-text-primary disabled:cursor-not-allowed"
          }
        >
          ×
        </button>
      </div>
    );
  }

  // Minimum-2-char hint matches the BE search short-circuit
  // (``backend/sales/search_views.py:_MIN_QUERY_LENGTH``) so the
  // picker doesn't sit silent on a 1-char input.
  const showMinHint = query.trim().length === 1;

  return (
    <div className="relative">
      <input
        role="combobox"
        type="text"
        value={query}
        onChange={onTextChange}
        placeholder={placeholder}
        disabled={disabled}
        aria-expanded={results.length > 0 || queried}
        aria-autocomplete="list"
        className={
          "w-full rounded-md border border-border-default bg-raised "
          + "px-3 py-1.5 text-sm text-text-primary "
          + "placeholder:text-text-tertiary outline-none "
          + "transition-fast ease-out "
          + "focus-visible:border-teal focus-visible:shadow-focus"
        }
      />
      {showMinHint && (
        <div
          className={
            "absolute left-0 right-0 top-full z-20 mt-1 rounded-md "
            + "border border-border-default bg-raised px-3 py-2 "
            + "text-sm text-text-tertiary shadow-overlay"
          }
        >
          Type at least 2 letters to search.
        </div>
      )}
      {!showMinHint && queried && results.length === 0
       && query.trim() !== "" && (
        <div
          className={
            "absolute left-0 right-0 top-full z-20 mt-1 rounded-md "
            + "border border-border-default bg-raised px-3 py-2 "
            + "text-sm text-text-tertiary shadow-overlay"
          }
        >
          No matches
        </div>
      )}
      {results.length > 0 && (
        <ul
          role="listbox"
          className={
            "absolute left-0 right-0 top-full z-20 mt-1 max-h-72 "
            + "overflow-auto rounded-md border border-border-default "
            + "bg-raised py-1 shadow-overlay"
          }
        >
          {results.map((row) => (
            <li
              key={`${row.kind}-${row.id}`}
              role="option"
              aria-selected={false}
              onMouseDown={(e) => {
                e.preventDefault();
                select(row);
              }}
              className={
                "flex cursor-pointer items-center gap-2 px-3 py-1.5 "
                + "text-sm text-text-primary hover:bg-recessed"
              }
            >
              <span
                className={
                  "rounded bg-recessed px-1.5 py-0.5 text-xs "
                  + "font-medium uppercase tracking-wide text-text-tertiary"
                }
              >
                {row.kind}
              </span>
              <span className="font-medium text-text-primary">
                {row.label}
              </span>
              {row.sublabel && (
                <span className="text-text-secondary">· {row.sublabel}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
